import { HttpClient } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ENVIRONMENT_SETTINGS, RedirectSourceEnum } from '@reconcept-platform/env';
import { generateCodeChallenge } from '@reconcept-platform/shared/util';
import { type Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import {
  AUTH_API_URL,
  AUTH_APP_ID,
  AUTH_APP_URL,
  AUTH_REDIRECT_SOURCE,
  AUTH_REDIRECT_URI,
  AUTH_SCOPE,
} from './shared-authentication.types';

interface OtpResponse {
  status: string;
  oidc?: any;
  user?: { email: string; id: string };
  error?: string;
}

export type AuthorizationStatus = 'error' | 'second_factor' | 'ok' | 'oidc';
export type AuthorizationError = 'invalid_credentials';
export type AuthorizationLockAction =
  'user_locked' | 'locked_because_invalid_credentials' | 'next_invalid_locks_account';

export interface AuthorizationResponse {
  status: AuthorizationStatus;
  user: { email: string; id: string };
  second_factor?: any;
  redirect_uri?: string;
  error?: AuthorizationError;
  failed_attempts: number;
  max_failed_attempts: number;
  lockAction: AuthorizationLockAction;
  oidc?: any;
}

export interface AuthorizationParams {
  client_id: string;
  redirect_uri: string;
  response_type: 'code';
  scope: string;
  code_challenge: string;
  code_challenge_method: 'S256';
  state?: string;
  source?: RedirectSourceEnum;
  redirect?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthorizedRedirectResponse {
  redirect_uri: string | null;
  external: boolean;
  status?: AuthorizationStatus | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private http = inject(HttpClient);
  private appId = inject(AUTH_APP_ID);
  private appRedirectURI = inject(AUTH_REDIRECT_URI);
  private appRedirectSource = inject(AUTH_REDIRECT_SOURCE);
  private authScope = inject(AUTH_SCOPE);
  private authApiUrl = inject(AUTH_API_URL);
  private authAppUrl = inject(AUTH_APP_URL);
  private environmentSettings = inject(ENVIRONMENT_SETTINGS);

  // stores the query parameters if authorization fails, so they can be reused after login
  storedQueryParameters: AuthorizationParams | undefined;

  private translateService = inject(TranslateService);

  //
  // Login app
  //

  getSSOUrl(source: 'web' | 'app', type: 'surfconext' | 'azure_f5aa1fac-5ca1-4342-bb45-647d88818a6f'): string {
    const redirectUri = encodeURIComponent(this.authAppUrl + '/oidc/' + type + '/code');
    return (
      `${this.authApiUrl}/oidc/` +
      type +
      (this.storedQueryParameters && this.storedQueryParameters.state
        ? `?state=${this.storedQueryParameters.state}&state=${source}&redirect_uri=${redirectUri}`
        : `?redirect_uri=${redirectUri}&state=${source}`)
    );
  }

  connectOidcToUserInSession() {
    return this.http.post(this.authApiUrl + '/oidc', {}, { withCredentials: true });
  }

  // returns a redirect_uri back to the requesting app on success
  loginAndAuthorizeForRedirect(
    email: string,
    password: string,
    source?: RedirectSourceEnum,
  ): Observable<AuthorizedRedirectResponse | null> {
    return this.login(email, password).pipe(
      mergeMap((data: AuthorizationResponse) => {
        if (data.status === 'error') {
          return throwError(() => ({
            ...data,
            lockAction: this.determineLockAction(data),
          }));
        }

        if (data.status === 'second_factor') {
          // empty redirect URI, to let caller handle redirect themselves. Since this method is called
          // from different contexts, expected redirect_uri could be different
          return of({ redirect_uri: '', external: false, status: data.status as AuthorizationStatus });
        }

        if (data.oidc && !data.user) {
          return of({ redirect_uri: '../login-to-connect', external: false, oidc: data.oidc });
        }

        return this.tryStoredQueryParamsOrDefaultRedirect(source);
      }),
    );
  }

  // login from the Login app
  // logs the user in based on their credentials
  // and tries to authorize based on stored query parameters (from the requesting app)

  submitOtp(otp: string, source?: RedirectSourceEnum) {
    return this.http
      .put<OtpResponse>(this.authApiUrl + '/session', { second_factor: { otp } }, { withCredentials: true })
      .pipe(
        tap((response: OtpResponse) => {
          if (response.error === 'invalid_second_factor') {
            throw Error(this.translateService.instant('qz-entered-otp-invalid-zm'));
          }
        }),
        mergeMap(() => this.tryStoredQueryParamsOrDefaultRedirect(source)),
      );
  }

  getRedirectSource(): RedirectSourceEnum | undefined {
    return this.storedQueryParameters ? this.storedQueryParameters.source : undefined;
  }

  // assumes a valid login session
  authorize(params: AuthorizationParams): Observable<string> {
    // store the queryparameters for reusing later
    this.storedQueryParameters = params;

    return this.http
      .post<AuthorizationResponse>(this.authApiUrl + '/oauth/authorize', params, {
        withCredentials: true,
      })
      .pipe(
        map((data) => {
          if (data.status === 'error') {
            throw Error('Authorization failed');
          }

          return data.redirect_uri ?? '';
        }),
      );
  }

  // perform authorization call for a certain application

  // check the current session
  getSession(): Observable<AuthorizationResponse> {
    return this.http.get(this.authApiUrl + '/session', {
      withCredentials: true,
    }) as Observable<AuthorizationResponse>; // TODO strictNullChecks make sure somehow that this cast is valid
  }

  // destroy the current session
  logout(accessToken: string, params?: AuthorizationParams) {
    // store the queryparameters for reusing later
    if (params) {
      this.storedQueryParameters = params;
    }

    // session storage is not available in SSR
    if (typeof sessionStorage !== 'undefined') {
      // clear the session storage, where things like preferred language is stored
      sessionStorage?.clear();
    }

    return this.http.delete(this.authApiUrl + '/session', {
      withCredentials: true,
      body: {
        access_token: accessToken,
      },
    });
  }

  // Trade in authorization token for app bearer token after returning from login
  requestAccessToken(codeVerifier: string, code: string): Observable<AuthTokens> {
    return this.http
      .post(this.authApiUrl + '/oauth/token', {
        client_id: this.appId,
        code: code,
        code_verifier: codeVerifier,
        grant_type: 'authorization_code',
        redirect_uri: this.appRedirectURI,
      })
      .pipe(
        map((data: any) => {
          return {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          };
        }),
      );
  }

  //
  // Client app
  //

  tryAccessTokenOrRefresh(accessToken: string, refreshToken: string): Observable<AuthTokens | null> {
    // first check if accessToken is still valid
    return this.introspectAccessToken(accessToken).pipe(
      mergeMap((accessTokenIsValid) => {
        // if token is valid, continue
        if (accessTokenIsValid) {
          return of({
            accessToken: accessToken,
            refreshToken: refreshToken,
          });
        }

        // if accessToken invalid, and no refreshToken, return null
        if (!refreshToken) {
          return of(null);
        }

        // try to refresh the token
        return this.refreshAccessToken(refreshToken);
      }),
    );
  }

  // returns success boolean
  refreshAccessToken(refreshToken: string): Observable<AuthTokens | null> {
    return this.http
      .post(this.authApiUrl + '/oauth/token', {
        client_id: this.appId,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
        redirect_uri: this.appRedirectURI,
      })
      .pipe(
        map((data: any) => {
          return {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          };
        }),
        catchError(() => {
          return of(null);
        }),
      );
  }

  // Use refresh_token to refresh the access token

  // temporary extraQueryParams, need to add redirect param to show notification to user if automatic redirect from v2.
  async getQueryParameters(
    codeVerifier: string,
    state?: string,
    queryParamsForLogin?: any,
  ): Promise<AuthorizationParams> {
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    let queryParams: AuthorizationParams = {
      client_id: this.appId,
      redirect_uri: this.appRedirectURI,
      response_type: 'code',
      scope: this.authScope,
      code_challenge: codeChallenge, // code_challenge URLsafe base64 encoding (without padding) of the  SHA256 hash of a random string
      code_challenge_method: 'S256',
      source: this.getCurrentRedirectSource(),
    };

    // make sure state is not included as undefined
    // we pass state as the encoded current route that the user is on so we can return them there after successful login
    if (!!state && !queryParams.state) {
      queryParams.state = this.encodeState(state);
    }

    // after state encoding, we don't want the extra query params to exist after login
    queryParams = {
      ...queryParams,
      ...queryParamsForLogin,
    };

    return queryParams;
  }

  // get query params

  async getQueryParametersAsURLParams(
    codeVerifier: string,
    state?: string,
    queryParamsForLogin?: any,
  ): Promise<URLSearchParams> {
    const queryParams = await this.getQueryParameters(codeVerifier, state, queryParamsForLogin);
    // transform params
    return this.transformQueryParametersToUrlParams(queryParams);
  }

  private transformQueryParametersToUrlParams(queryParams: AuthorizationParams) {
    const params = new URLSearchParams();
    // tslint:disable-next-line:forin
    for (const key in queryParams) {
      const value = queryParams[key as keyof AuthorizationParams];
      if (value) {
        params.set(key, value);
      }
    }

    return params;
  }

  // urlsafe base64 of state
  encodeState(state: string) {
    return encodeURIComponent(btoa(state));
  }

  // Helper methods

  decodeState(encodedState: string) {
    try {
      return atob(decodeURIComponent(encodedState));
    } catch (e) {
      return '';
    }
  }

  determineLockAction(data: AuthorizationResponse): AuthorizationLockAction | null {
    if (!data.failed_attempts || !data.max_failed_attempts) {
      return null;
    }

    if (data.failed_attempts === data.max_failed_attempts - 1) {
      return 'next_invalid_locks_account';
    }

    if (data.failed_attempts === data.max_failed_attempts) {
      return 'locked_because_invalid_credentials';
    }

    if (data.failed_attempts > data.max_failed_attempts) {
      return 'user_locked';
    }

    return null;
  }

  loginByToken(token: string) {
    return this.http.post(
      this.authApiUrl + '/session',
      {
        session: {
          invite_token: token,
        },
      },
      {
        withCredentials: true,
      },
    );
  }

  private login(email: string, password: string) {
    return this.http.post<any>(
      this.authApiUrl + '/session',
      {
        session: {
          email,
          password,
        },
      },
      {
        withCredentials: true,
      },
    );
  }

  private getDefaultRedirectUriForSource(source?: RedirectSourceEnum): string {
    // by redirecting to /login/success we make sure that if there is an existing valid access token for another session, it is ignored in the application and replaced with the fresh token for the user we just logged in
    if (source === RedirectSourceEnum.PORTFOLIO) {
      return `${this.environmentSettings.portfolioAppURL}/login/success`;
    } else if (source === RedirectSourceEnum.WORKSPACE_DASHBOARD) {
      return `${this.environmentSettings.workspaceAppURL}/login/success`;
    } else {
      return '';
    }
  }

  /**
   * always external redirect
   * @param source
   * @private
   */
  private tryStoredQueryParamsOrDefaultRedirect(
    source?: RedirectSourceEnum,
  ): Observable<AuthorizedRedirectResponse | null> {
    if (this.storedQueryParameters) {
      // immediately try to authorize
      return this.authorize(this.storedQueryParameters).pipe(
        // in case we error here, return null
        catchError(() => of(null)),
        map((redirect_uri) => ({ redirect_uri, external: true, status: 'ok' as AuthorizationStatus })),
      );
    } else {
      // if we don't have stored parameters, get a default redirect uri
      return of({
        redirect_uri: this.getDefaultRedirectUriForSource(source),
        external: true,
        status: 'ok' as AuthorizationStatus,
      });
    }
  }

  // returns success boolean
  private introspectAccessToken(accessToken: string): Observable<boolean> {
    return this.http
      .post<any>(this.authApiUrl + '/oauth/introspect', {
        client_id: this.appId,
        token: accessToken,
      })
      .pipe(
        catchError((error) => {
          if (
            isDevMode() &&
            error.status !== 0 &&
            error.error &&
            error.error.includes?.('ActiveRecord::PendingMigrationError')
          ) {
            // redirect to the rails error page if we have a pending migration
            window.location.href = this.authApiUrl + '/oauth/introspect';
          }
          return throwError(() => error);
        }),

        map((response: any) => {
          // return whether this is an active token
          return response.active;
        }),
      );
  }

  // determine the source pointing to the current app by the appRedirectURI
  private getCurrentRedirectSource(): RedirectSourceEnum {
    return this.appRedirectSource;
  }
}
